import React, { useState } from 'react';
import { ArrowRight, Code, Users, Zap, Target, Rocket, Award, TrendingUp, CheckCircle} from 'lucide-react';
import ContactForm from './ContactForm';

const Hero = ({ setActiveSection, setShowContact }) => {
  const [showContact, setShowContactState] = useState(false);

  const stats = [
    { number: '25+', label: 'Projects Delivered', icon: Rocket },
    { number: '50+', label: 'Students Trained', icon: Users },
    { number: '5+', label: 'Industry Experts', icon: Award },
    { number: '98%', label: 'Client Satisfaction', icon: TrendingUp }
  ];

  const features = [
    { icon: Code, title: 'Cutting-Edge Solutions', desc: 'Latest technologies for modern challenges' },
    { icon: Users, title: 'Expert Team', desc: 'Industry professionals with proven expertise' },
    { icon: Zap, title: 'Rapid Delivery', desc: 'Fast turnaround without compromising quality' },
    { icon: Target, title: 'Excellence Guaranteed', desc: 'Quality assurance at every step' }
  ];

  const whyChooseUs = [
    { title: 'Industry-Leading Expertise', desc: 'Years of experience across diverse technologies and domains' },
    { title: 'Student-Centric Approach', desc: 'Dedicated programs for internships, training, and career guidance' },
    { title: 'Innovative Solutions', desc: 'Leveraging AI/ML and cutting-edge tech for transformation' },
    { title: 'Agile Development', desc: 'Flexible methodologies ensuring timely delivery' },
    { title: 'End-to-End Support', desc: 'From concept to deployment and beyond' },
    { title: '24/7 Assistance', desc: 'Round-the-clock support for all technical needs' }
  ];


  return (
    <div className="min-h-screen relative">
      {/* Hero Section with Gradient Background */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233b82f6' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>

        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative z-10">
              <div className="inline-flex items-center space-x-2 bg-white shadow-sm border border-gray-200 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-1 mt-2 hover:shadow-md transition-all">
                <img
                  src="/logo.png"
                  alt="AUBRYNT"
                  className="w-4 h-4"
                />
                <span>Software Solutions & Student Development</span>
              </div>

              <h1 className="text-4xl lg:text-[10rem] font-bold leading-tight mb-6 text-gray-900">
                Empowering <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Minds</span>, Elevating <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Ideas</span>
              </h1>

              <p className="text-lg lg:text-3xl text-gray-600 mb-8 leading-relaxed">
                Premier software development company specializing in AI/ML, Full Stack, Mobile Apps, and empowering students through mentorship and training programs.
              </p>

              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => setActiveSection('services')}
                  className="group px-8 py-4 lg:px-12 lg:py-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold lg:text-xl hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center space-x-2 shadow-lg"
                >
                  <span>Software Services</span>
                  <ArrowRight className="w-5 h-5 lg:w-7 lg:h-7 group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={() => setActiveSection('students')}
                  className="px-8 py-4 lg:px-12 lg:py-6 bg-white border-2 border-gray-200 text-gray-900 rounded-xl font-semibold lg:text-xl hover:border-blue-600 hover:text-blue-600 hover:shadow-lg transition-all duration-300"
                >
                  Students
                </button>
              </div>
            </div>

            <div className="relative z-10">
              <div className="space-y-4">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <div
                      key={index}
                      className="group flex items-start space-x-4 p-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:scale-105 transition-all duration-300"
                    >
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 mb-1 text-lg">{feature.title}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{feature.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-20">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  className="group text-center p-8 bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:scale-105 transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-md">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">{stat.number}</div>
                  <div className="text-gray-600 font-medium text-sm">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%239333ea' fill-opacity='0.05' fill-rule='evenodd'/%3E%3C/svg%3E")`
        }}></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose AUBRYNT?</h2>
            <p className="text-xl text-gray-600">Excellence in software development and student empowerment</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <div
                key={index}
                className="group bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2 text-lg">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-purple-600 to-blue-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.2'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Transform Your Vision?</h2>
          <p className="text-xl mb-8 opacity-90">Let's collaborate and create something extraordinary together</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => setShowContact(true)}
              className="px-8 py-4 bg-white text-blue-400 rounded-xl font-semibold hover:shadow-2xl transition-all duration-300 hover:scale-105 shadow-xl"
            >
              Get Started Now
            </button>
            <button
              onClick={() => setActiveSection('projects')}
              className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-semibold hover:shadow-xl transition-all duration-300 hover:scale-105 shadow-lg"
            >
              View Our Work
            </button>
          </div>
        </div>
      </section>

      {/* Contact Form Modal */}
      {showContact && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 mt-16">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Contact Us</h3>
                <button
                  onClick={() => setShowContactState(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
              <ContactForm />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Hero;
